import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Plus, Trash2, Users, Search, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  superadmin: 'SuperAdmin',
  admin: 'Admin',
  operator: 'Operator',
  technician: 'Texnik',
  accountant: 'Mühasib',
  d2d_agent: 'D2D Agent',
  lawyer: 'Hüquqşunas',
  network_admin: 'NetAdmin',
  system_admin: 'SysAdmin',
};

export default function ModuleAccessPanel() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [addSearch, setAddSearch] = useState('');

  const fetchModules = useCallback(async () => {
    setLoadingModules(true);
    try {
      const res = await axios.get(`${API}/admin/modules`, { headers });
      setModules(res.data || []);
    } catch (e) {
      toast.error('Modul siyahısı yüklənmədi');
    } finally {
      setLoadingModules(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAssignedUsers = useCallback(async (moduleId) => {
    if (!moduleId) return;
    setLoadingUsers(true);
    try {
      const res = await axios.get(`${API}/admin/modules/${moduleId}/users`, { headers });
      setAssignedUsers(res.data || []);
    } catch (e) {
      toast.error('İstifadəçilər yüklənmədi');
    } finally {
      setLoadingUsers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAvailableUsers = useCallback(async (moduleId) => {
    if (!moduleId) return;
    try {
      const res = await axios.get(`${API}/admin/modules/${moduleId}/available-users`, { headers });
      setAvailableUsers(res.data || []);
    } catch (e) {
      toast.error('Əlavə edilə bilən istifadəçilər yüklənmədi');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  useEffect(() => {
    if (selectedModule) {
      fetchAssignedUsers(selectedModule.id);
    }
  }, [selectedModule, fetchAssignedUsers]);

  const handleSelectModule = (mod) => {
    setSelectedModule(mod);
    setSearch('');
  };

  const handleOpenAddDialog = async () => {
    await fetchAvailableUsers(selectedModule.id);
    setAddSearch('');
    setShowAddDialog(true);
  };

  const handleAssign = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/modules/${selectedModule.id}/users`,
        { user_id: userId },
        { headers }
      );
      toast.success('İstifadəçi əlavə edildi');
      await Promise.all([
        fetchAssignedUsers(selectedModule.id),
        fetchAvailableUsers(selectedModule.id),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Əlavə edilmədi');
    }
  };

  const handleRemove = async (userId, username) => {
    if (!window.confirm(`"${username}" istifadəçisini "${selectedModule.label}" modulundan silmək istədiyinizə əminsiniz?`)) {
      return;
    }
    try {
      await axios.delete(
        `${API}/admin/modules/${selectedModule.id}/users/${userId}`,
        { headers }
      );
      toast.success('İstifadəçi silindi');
      await fetchAssignedUsers(selectedModule.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const filteredAssigned = assignedUsers.filter((u) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.username || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  });

  const filteredAvailable = availableUsers.filter((u) => {
    if (!addSearch) return true;
    const q = addSearch.toLowerCase();
    return (
      (u.username || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="module-access-panel">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-purple-600" />
        <div>
          <h2 className="text-2xl font-bold">Modul İcazələri</h2>
          <p className="text-sm text-zinc-600">
            Hər modula kimin daxil ola biləcəyini idarə edin. SuperAdmin avtomatik bütün modullara çatır.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Modules list */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Modullar</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loadingModules && <div className="text-sm text-zinc-500">Yüklənir...</div>}
            {!loadingModules && modules.length === 0 && (
              <div className="text-sm text-zinc-500">Modul yoxdur</div>
            )}
            {modules.map((m) => (
              <button
                key={m.id}
                onClick={() => handleSelectModule(m)}
                data-testid={`module-item-${m.id}`}
                className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                  selectedModule?.id === m.id
                    ? 'bg-purple-50 border-purple-400 text-purple-900'
                    : 'bg-white border-zinc-200 hover:bg-zinc-50'
                }`}
              >
                <div className="font-medium">{m.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{m.description}</div>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* RIGHT: Assigned users */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4" />
                {selectedModule ? `${selectedModule.label} — İstifadəçilər` : 'Modul seçin'}
              </CardTitle>
              {selectedModule && (
                <p className="text-xs text-zinc-500 mt-1">
                  Bu modula çıxışı olan {assignedUsers.length} istifadəçi
                </p>
              )}
            </div>
            {selectedModule && (
              <Button
                onClick={handleOpenAddDialog}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="add-user-to-module-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> İstifadəçi əlavə et
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {!selectedModule && (
              <div className="text-sm text-zinc-500 py-8 text-center">
                Sol tərəfdən bir modul seçin
              </div>
            )}
            {selectedModule && (
              <>
                <div className="relative mb-3">
                  <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
                  <Input
                    placeholder="İstifadəçi axtar (ad, username, rol)..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                    data-testid="module-users-search"
                  />
                </div>

                {loadingUsers && <div className="text-sm text-zinc-500">Yüklənir...</div>}
                {!loadingUsers && filteredAssigned.length === 0 && (
                  <div className="text-sm text-zinc-500 py-6 text-center">
                    {assignedUsers.length === 0
                      ? 'Bu modula heç bir istifadəçi əlavə edilməyib'
                      : 'Axtarışa uyğun nəticə yoxdur'}
                  </div>
                )}
                <div className="space-y-2">
                  {filteredAssigned.map((u) => (
                    <div
                      key={u.id}
                      className="flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2 hover:bg-zinc-50"
                      data-testid={`assigned-user-${u.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{u.full_name || u.username}</div>
                        <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                          <span>@{u.username}</span>
                          <Badge variant="secondary" className="text-xs">
                            {ROLE_LABELS[u.role] || u.role}
                          </Badge>
                          {u.is_active === false && (
                            <Badge variant="destructive" className="text-xs">Deaktiv</Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        onClick={() => handleRemove(u.id, u.username)}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-user-${u.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle>
              "{selectedModule?.label}" moduluna istifadəçi əlavə et
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-400" />
              <Input
                placeholder="Axtar..."
                value={addSearch}
                onChange={(e) => setAddSearch(e.target.value)}
                className="pl-9"
                data-testid="available-users-search"
              />
            </div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {filteredAvailable.length === 0 && (
                <div className="text-sm text-zinc-500 py-6 text-center">
                  {availableUsers.length === 0
                    ? 'Əlavə edilə bilən istifadəçi qalmayıb'
                    : 'Axtarışa uyğun nəticə yoxdur'}
                </div>
              )}
              {filteredAvailable.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between border border-zinc-200 rounded-md px-3 py-2 hover:bg-zinc-50"
                  data-testid={`available-user-${u.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{u.full_name || u.username}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
                      <span>@{u.username}</span>
                      <Badge variant="secondary" className="text-xs">
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleAssign(u.id)}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid={`assign-user-${u.id}`}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Əlavə et
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
